import { IconButton, Stack, useTheme } from '@mui/material'
import MaterialReceptionHeader from './MaterialReceptionHeader'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { enumFilterOptions, systemValueLabel } from '../generic/systemValues/systemValueLabel'
import AlertUI from '../generic/AlertUI'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { useEffect, useState } from 'react'
import { apiPut, apiGet } from '../generic/Api_Functions'
import SmartTextField from '../generic/smarttextfield/SmartTextField'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import { TextSnippetOutlined } from '@mui/icons-material'
import { DefaultAccordion } from '../generic/DefaultAccordion'
import Document from '../configuration/document/Document'
import { useNavigate } from 'react-router-dom'
import WarningIcon from '@mui/icons-material/Warning'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import { getMaterialReceptionColumns } from './MaterialReceptionExtendedColumnsUtil'
import SmartSelect from '../generic/smartSelect/SmartSelect'

/**
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNoteDataAction} DeliveryNoteDataAction DataAction object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNote} DeliveryNote DeliveryNote object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').PurchaseOrderReception} PurchaseOrderReception PurchaseOrderReception object
 */

/**
 *
 * @param {object} props
 * @param {DeliveryNote} props.deliveryNote
 * @param {React.Dispatch<DeliveryNoteDataAction>} props.dispatchDeliveryNote
 * @param {PurchaseOrderReception[]} props.allReceptions
 * @param {string} props.lastQrVersion
 * @param {import('../generic/utilities/objectStructures/materialreception/materialReception').Requirement[]} props.requirements
 * @returns {JSX.Element}
 */
const ReportQuantity = ({ deliveryNote, dispatchDeliveryNote, allReceptions, lastQrVersion, rawMaterialExtendedColumns }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  /** @type {[PurchaseOrderReception[], React.Dispatch<SetStateAction<PurchaseOrderReception[]>>]} */
  const [reportedData, setReportedData] = useState(allReceptions)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [realRow, setRealRow] = useState(null)
  const [theoreticalRow, setTheoreticalRow] = useState(null)
  const theme = useTheme()
  const isUserAuthorized = useAuthorization()
  const [storehouses, setStorehouses] = useState([])

  useEffect(() => {
    setReportedData(allReceptions)
  }, [deliveryNote?.id, allReceptions])

  const style = {
    minWidth: '80rem',
    width: '100%',
    display: 'grid',
    gap: '1rem',
    backgroundColor: theme.palette.background.row2,
    padding: '1rem'
  }
  const navigate = useNavigate()

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiGet('storehouses')
        setStorehouses(data || [])
      } catch (err) {
        console.error('failed to fetch storehouses', err)
        setStorehouses([])
      }
    }
    load()
  }, [])

  const columns = [
    {
      name: t_('Status'),
      field: 'status',
      render: (row) => systemValueLabel(t_, 'PurchaseOrderTheoreticalReceptionStatusEnum', row.status),
      filterByLabel: { options: enumFilterOptions(t_, 'PurchaseOrderTheoreticalReceptionStatusEnum') }
    },
    {
      name: t_('Supplier code'),
      field: 'supplier.code'
    },
    {
      name: t_('Supplier name'),
      field: 'supplier.name'
    },
    {
      name: t_('Record code'),
      field: 'recordCode'
    },
    {
      name: t_('Record position'),
      field: 'recordPos'
    },
    {
      name: t_('Order'),
      field: 'order'
    },
    {
      name: t_('Pos / Line'),
      field: 'lineOrderPosition'
    },
    {
      name: t_('Raw material reference'),
      field: 'rawMaterialReference'
    },
    {
      name: t_('Revision'),
      field: 'revision'
    },
    {
      name: t_('Batch'),
      field: 'batchNumber',
      render: (row) => {
        if (!row.serializable || !deliveryNote?.id) {
          return row.batchNumber
        }
        const currentRow = reportedData.find(data => data.id === row.id)
        const hasQtyValue = currentRow?.receivedQuantity !== undefined && currentRow?.receivedQuantity !== null && currentRow?.receivedQuantity !== ''
        const qty = hasQtyValue ? Number(currentRow.receivedQuantity) : null
        const isZeroQty = hasQtyValue && !Number.isNaN(qty) && qty === 0
        if (!currentRow) {
          return ''
        }
        return (
          <SmartTextField
            value={isZeroQty ? '' : currentRow.batchNumber}
            onChange={(value) => {
              if (isZeroQty) {
                return
              }
              const newData = [...reportedData]
              const index = newData.findIndex(data => data.id === row.id)
              newData[index].batchNumber = value
              setReportedData(newData)
            }}
            checkError={(value) => {
              if (!row.serializable) {
                return ''
              }
              if (!hasQtyValue || isZeroQty) {
                return ''
              }
              if (!value && !Number.isNaN(qty) && qty > 0) {
                return t_('Batch is mandatory when quantity is greater than 0')
              }
              return ''
            }}
            disabled={isZeroQty || row.locked}
            checkAuto
          />
        )
      }
    },
    {
      name: t_('Project'),
      field: 'projectCode',
      readOnly: true
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Received theoretical quantity'),
      field: 'receivedTheoreticalQuantity',
      search: false,
      disableFilter: true,
      render: (row) => {
        if (row.serializable) {
          return '1 / ' + row.receivedTheoreticalQuantity
        }
        return row.receivedTheoreticalQuantity
      }
    },
    {
      name: t_('Received quantity'),
      field: 'receivedQuantityView',
      render: (row) => {
        if (!deliveryNote?.id) {
          return row.receivedQuantity
        }
        return (
          <SmartTextField
            placeholder={row.serializable ? '0 or 1' : ''}
            value={reportedData.find(data => data.id === row.id)?.receivedQuantity}
            onChange={(value) => {
              const targetRow = reportedData.find(data => data.id === row.id)

              if (targetRow.serializable) {
                if (value !== '' && value !== '0' && value !== '1') {
                  return
                }
              }

              const newData = [...reportedData]
              const index = newData.findIndex(data => data.id === row.id)
              newData[index].receivedQuantity = value
              newData[index].receivedQuantityView = value
              const hasValue = value !== undefined && value !== null && value !== ''
              if (hasValue && newData[index].serializable) {
                const numVal = Number(value)
                if (!isNaN(numVal)) {
                  if (numVal === 0) {
                    newData[index].batchNumber = ''
                  }
                }
              }
              setReportedData(newData)
            }}
            checkError={(value) => {
              if (value === undefined || value === null || value === '') {
                return ''
              }
              const currentRow = reportedData.find(data => data.id === row.id)
              const numVal = Number(value)

              if (currentRow.serializable) {
                if (isNaN(numVal) || (numVal !== 0 && numVal !== 1)) {
                  return t_('Quantity must be 0 or 1')
                }
              } else {
                if (isNaN(numVal) || numVal < 0) {
                  return t_('Quantity cannot be negative')
                }
              }

              if (currentRow.serializable && numVal > 0 && !currentRow.batchNumber) {
                return t_('Batch is mandatory when quantity is greater than 0')
              }

              return ''
            }}
            checkAuto
            disabled={row.locked}
          />
        )
      }
    },
    {
      name: t_('Storehouse'),
      field: 'storehouse.name',
      type: 'smartSelect',
      render: (row) => {
        if (!deliveryNote?.id) {
          return row.storehouse?.name || ''
        }
        return (
          <SmartSelect
            value={row.storehouse?.name || null}
            onChange={(value) => {
              const selected = value != null ? storehouses.find(s => s.name === value) : null
              const newData = [...reportedData]
              const index = newData.findIndex(data => data.id === row.id)
              newData[index].storehouse = selected
              setReportedData(newData)
            }}
            selectableOptions={storehouses.map(s => s.name)}
            disabled={row.locked}
            autoSelectFirst={false}
            disableClearable={false}
          />
        )
      }
    }
  ]
  const getColumns = () =>
    getMaterialReceptionColumns({
      columns,
      extendedColumns: rawMaterialExtendedColumns,
      editIndex: null
    })
  const onSave = async (number, date) => {
    let correctFormat = true
    reportedData.forEach((element) => {
      if (element.receivedQuantity === undefined || element.receivedQuantity === null || element.receivedQuantity === '') {
        return
      }
      const qty = Number(element.receivedQuantity)
      if (isNaN(qty) || qty < 0) {
        correctFormat = false
      }
      if (element.serializable && qty > 0 && !element.batchNumber) {
        correctFormat = false
      }
    })

    if (!correctFormat) {
      showAlert({ severity: 'error', title: t_('There are some incorrect values. Fix them before saving') })
      return false
    }

    // for (let index = 0; index < deliveryNote.purchaseOrderTheoreticalReceptions.length; index++) {
    //  const reported = reportedData.filter(data => data.purchaseOrderTheoreticalReceptionId === deliveryNote.purchaseOrderTheoreticalReceptions[index].id)
    //  for (let index2 = 0; index2 < reported.length; index2++) {
    //    if (reported[index2].batchNumber) {
    //      const duplicate = reported.find(reception => reception.batchNumber === reported[index2].batchNumber && reception.id !== reported[index2].id)
    //      if (duplicate) {
    //        showAlert({ severity: 'error', title: t_('There are some incorrect values. Fix them before saving') })
    //        return false
    //      }
    //    }
    //  }
    // }
    const data = { ...deliveryNote }
    data.date = date
    data.number = number
    data.purchaseOrderTheoreticalReceptions.forEach(purchaseOrderTheoreticalReception => {
      purchaseOrderTheoreticalReception.purchaseOrderReceptions = reportedData.filter(data => data.purchaseOrderTheoreticalReceptionId === purchaseOrderTheoreticalReception.id)
    })
    const result = await apiPut('deliveryNote/update/' + lastQrVersion + '/' + false, data)
    dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: result })
    const data2 = []
    result.purchaseOrderTheoreticalReceptions.forEach((purchaseOrderTheoreticalReception) => {
      if (purchaseOrderTheoreticalReception.purchaseOrderReceptions) {
        data2.push(...purchaseOrderTheoreticalReception.purchaseOrderReceptions)
      }
    })
    setReportedData(data2)
    return true
  }
  const actions = {
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          const realData = { id: row.id, name: 'Position: ' + row.recordPos + '  ||   Order: ' + row.order + '  ||   Line: ' + row.lineOrderPosition }
          setRealRow(realData)
          const theoreticalData = { id: row.purchaseOrderTheoreticalReceptionId, name: 'Order: ' + row.order + '  ||   Line: ' + row.lineOrderPosition }
          setTheoreticalRow(theoreticalData)
          setShowDocument(true)
        },
        disabled: !isUserAuthorized('DOCUMENTS_PURCHASE_ORDER_REAL_RECEPTION')
      },
      {
        name: 'Incidence',
        render: (row) => {
          return (
            <IconButton>
              <WarningIcon />
            </IconButton>
          )
        },
        onClick: (row) => {
          navigate('/mos/reception/incidence', { state: { deliveryNote, type: 'QUALITY/QUANTITY', currentSelectedId: row.id, window: 'REAL_QUANTITY' } })
        },
        disabled: (row) => !isUserAuthorized('RECEPTION_INCIDENCE') || row.type === 'MANUAL'
      }
    ]
  }

  const renderAccordion = (header, reference, defaultExpanded, resourceType) => {
    return (
      <DefaultAccordion headerRender={t_(header)} defaultExpanded={defaultExpanded} disabled={!isUserAuthorized('DOCUMENTS_' + resourceType)}>
        <Document reference={reference} resourceType={resourceType} isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />
      </DefaultAccordion>
    )
  }

  const dataFetch = async (requestPagination) => {
    if (deliveryNote?.id) {
      return reportedData
    }
    return apiPut('purchaseOrderReception/getAll', requestPagination)
  }

  return (
    <Stack>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <div style={style}>
            {renderAccordion('Delivery Note', { id: deliveryNote?.id, name: deliveryNote?.number }, false, 'DELIVERY_NOTE')}
            {renderAccordion('Theoretical Quantity', theoreticalRow, false, 'PURCHASE_ORDER_THEORETICAL_RECEPTION')}
            {renderAccordion('Real Quantity', realRow, true, 'PURCHASE_ORDER_REAL_RECEPTION')}
            {renderAccordion('Qualitative Quantity', realRow, false, 'PURCHASE_ORDER_QUALITATIVE_RECEPTION')}
          </div>
        }
        setOpen={showDocument}
      />
      {AlertElement}
      <MaterialReceptionHeader
        deliveryNote={deliveryNote}
        dispatchDeliveryNote={dispatchDeliveryNote}
        showAlert={showAlert}
        onSave={(number, date) => onSave(number, date)}
        window='REAL_QUANTITY'
      />
      <div style={{ marginTop: '1rem' }}>
        <SmartMateriaUITable
          tableTuneUniqueKey='ReportQuantity'
          columns={getColumns()}
          dataFetch={dataFetch}
          actions={actions}
          pageable={deliveryNote?.id ? undefined : true}
          stickyTopOffset={112}
        />
      </div>
    </Stack>
  )
}

export default ReportQuantity
